/**
 * 密度重建模块（ZSM-1.1 核心升级）
 *
 * 目的：从粗 10 分段表 + 边界锚点（前 300 名、资格线）出发，
 *      重建分数级（1 分一段）密度，得到任意段内累计位置。
 *
 * 三种方法：
 *   1. uniform   均匀分布（v1 退化基线，对照用）
 *   2. pchip     Piecewise Cubic Hermite Interpolating Polynomial（单调插值）
 *                Fritsch & Carlson, 1980, SIAM J. Numer. Anal.
 *   3. whittaker Whittaker–Henderson 修匀 / 等价于 Hodrick-Prescott 滤波
 *                Henderson 1924; Hodrick & Prescott 1997 (Edward Prescott
 *                获 2004 年诺贝尔经济学奖)
 *
 * 数学约束：
 *   - 所有方法在每段总人数上严格匹配官方分段表（无信息损失）
 *   - 锚点：前 300 名累计 = 300；低于资格线累计 = 总人数（即 CDF=1）
 *   - 单调约束：CDF 必须单调不减
 *
 * 输出：DensityModel.cumulativePositionInBand(score) ∈ [0,1]
 *       表示该分数在所属段内的累计位置（0=段顶，1=段底）
 */

import type { BandSegment, YearPolicy } from './types'

export type DensityMethod = 'uniform' | 'pchip' | 'whittaker'

export interface DensityModel {
  method: DensityMethod
  /** 在所属段内的累计位置，0 = 段内最高分（最前），1 = 段内最低分（最后） */
  cumulativePositionInBand: (score: number) => number
  /** 任意分数对应的全市累计名次（从 1 开始） */
  rankAtScore: (score: number) => number
  /** 每个 1 分的密度（人/分），用于绘制密度曲线 */
  densityAt: (score: number) => number
  /** 用于可视化：[{score, density}] 全局密度曲线点 */
  curve: () => Array<{ score: number; density: number }>
}

// ───────────────────────────────────────────────────────────────
// 工具：把政策的 segments 转成"按分数从高到低"的有序数组（理论上原数据就是这个顺序，但做一次防御）
// ───────────────────────────────────────────────────────────────
function orderedSegments(policy: YearPolicy): BandSegment[] {
  return [...policy.segments].sort((a, b) => b.upper - a.upper)
}

// ───────────────────────────────────────────────────────────────
// 方法 1：均匀分布（基线 / 对照）
// ───────────────────────────────────────────────────────────────
function buildUniformModel(policy: YearPolicy): DensityModel {
  const segs = orderedSegments(policy)
  return {
    method: 'uniform',
    cumulativePositionInBand: (score) => {
      const band = segs.find((s) => score >= s.lower && score < s.upper)
      if (!band) return score >= (policy.top300_line ?? Infinity) ? 0 : 1
      const width = band.upper - band.lower
      return (band.upper - score) / width
    },
    rankAtScore: (score) => {
      if (policy.top300_line !== null && score >= policy.top300_line) {
        return Math.max(1, Math.round(300 * (policy.top300_line - score + 1) / 100))
      }
      let cum = 300
      for (const s of segs) {
        if (s.upper > score) {
          if (score >= s.lower) {
            const portion = (s.upper - score) / (s.upper - s.lower)
            cum += s.people * portion
            return Math.round(cum)
          } else {
            cum += s.people
          }
        }
      }
      return Math.round(cum)
    },
    densityAt: (score) => {
      const band = segs.find((s) => score >= s.lower && score < s.upper)
      if (!band) return 0
      return band.people / (band.upper - band.lower)
    },
    curve: () => {
      const points: Array<{ score: number; density: number }> = []
      for (const s of segs) {
        const d = s.people / (s.upper - s.lower)
        for (let sc = s.lower; sc < s.upper; sc++) {
          points.push({ score: sc, density: d })
        }
      }
      return points
    }
  }
}

// ───────────────────────────────────────────────────────────────
// 方法 2：PCHIP（Piecewise Cubic Hermite Interpolating Polynomial）
// ───────────────────────────────────────────────────────────────
/**
 * 给定节点 (x_i, y_i) 严格单调（x 递增、y 不减），返回任意 x → y 的函数。
 * 算法采用 Fritsch–Carlson 切线修正以保持局部单调。
 */
function pchipInterpolant(xs: number[], ys: number[]): (x: number) => number {
  const n = xs.length
  if (n < 2) return () => ys[0] ?? 0

  // 计算每个区间斜率
  const h: number[] = new Array(n - 1)
  const delta: number[] = new Array(n - 1)
  for (let i = 0; i < n - 1; i++) {
    h[i] = xs[i + 1] - xs[i]
    delta[i] = (ys[i + 1] - ys[i]) / h[i]
  }

  // 计算每个节点的切线斜率（Fritsch–Carlson）
  const m: number[] = new Array(n).fill(0)
  m[0] = delta[0]
  m[n - 1] = delta[n - 2]
  for (let i = 1; i < n - 1; i++) {
    if (delta[i - 1] * delta[i] <= 0) {
      m[i] = 0
    } else {
      const w1 = 2 * h[i] + h[i - 1]
      const w2 = h[i] + 2 * h[i - 1]
      m[i] = (w1 + w2) / (w1 / delta[i - 1] + w2 / delta[i])
    }
  }

  return (x: number): number => {
    if (x <= xs[0]) return ys[0]
    if (x >= xs[n - 1]) return ys[n - 1]
    let i = 0
    while (i < n - 1 && xs[i + 1] < x) i++
    const t = (x - xs[i]) / h[i]
    const t2 = t * t
    const t3 = t2 * t
    const h00 = 2 * t3 - 3 * t2 + 1
    const h10 = t3 - 2 * t2 + t
    const h01 = -2 * t3 + 3 * t2
    const h11 = t3 - t2
    return h00 * ys[i] + h10 * h[i] * m[i] + h01 * ys[i + 1] + h11 * h[i] * m[i + 1]
  }
}

function buildPchipModel(policy: YearPolicy): DensityModel {
  const segs = orderedSegments(policy)
  if (segs.length === 0 || policy.top300_line === null || policy.qualification_line === null) {
    return buildUniformModel(policy)
  }

  // 构造 CDF 节点：每段的"下边界 → 累计人数（从 301 名起算）"
  // 锚点 1：前 300 名 → 在 top300_line 处累计 300
  // 锚点 2：低于资格线 → 累计 = 300 + sum(所有段)
  const totalSegPeople = segs.reduce((a, s) => a + s.people, 0)

  // 节点用"分数"作为 x、"累计人数（从顶向下）"作为 y
  // x 从大到小：top300_line（y=300），seg[0].lower（y=300+seg[0].people），seg[1].lower（y=300+seg[0]+seg[1])…
  // 为了 pchip 要 x 递增，把分数取负
  const xsDescending: number[] = [policy.top300_line]
  const ysAscending: number[] = [300]
  let cum = 300
  for (const s of segs) {
    cum += s.people
    xsDescending.push(s.lower)
    ysAscending.push(cum)
  }
  // 此时 xs 是递减的，ys 是递增的
  const xs = xsDescending.map((v) => -v)
  const ys = ysAscending
  const interp = pchipInterpolant(xs, ys)
  const totalPeople = 300 + totalSegPeople

  const rankAtScore = (score: number): number => {
    if (score >= policy.top300_line!) {
      return Math.max(1, Math.round(300 * (policy.top300_line! - score + 1) / 10 + 1))
    }
    if (score < policy.qualification_line!) return totalPeople
    return Math.round(interp(-score))
  }

  return {
    method: 'pchip',
    cumulativePositionInBand: (score) => {
      const band = segs.find((s) => score >= s.lower && score < s.upper)
      if (!band) return score >= policy.top300_line! ? 0 : 1
      const rankAtTop = interp(-band.upper)
      const rankAtBottom = interp(-band.lower)
      const rankAtHere = interp(-score)
      const denom = rankAtBottom - rankAtTop
      if (denom <= 0) return 0
      return Math.max(0, Math.min(1, (rankAtHere - rankAtTop) / denom))
    },
    rankAtScore,
    densityAt: (score) => {
      if (score < policy.qualification_line! || (policy.top300_line !== null && score >= policy.top300_line)) return 0
      // 数值差分得到密度
      const d = (interp(-(score - 0.5)) - interp(-(score + 0.5)))
      return Math.max(0, d) // 单调修正
    },
    curve: () => {
      const points: Array<{ score: number; density: number }> = []
      const minS = policy.qualification_line!
      const maxS = policy.top300_line!
      for (let sc = minS; sc < maxS; sc++) {
        const d = interp(-(sc - 0.5)) - interp(-(sc + 0.5))
        points.push({ score: sc, density: Math.max(0, d) })
      }
      return points
    }
  }
}

// ───────────────────────────────────────────────────────────────
// 方法 3：Whittaker–Henderson 修匀（HP 滤波等价形式）
// ───────────────────────────────────────────────────────────────
/**
 * 求解形如 z = (I + λ Dᵀ D)⁻¹ y 的线性系统（D 为 k 阶差分矩阵）。
 * 我们用 Thomas 算法 + 五对角扩展（k=2 时为五对角矩阵）。
 *
 * 但出于鲁棒性，这里用 Cholesky / LDLᵀ 分解（带状）。
 *
 * 数学：
 *   minimize ||y - z||² + λ ||D² z||²
 *   ⇔ (I + λ D²ᵀ D²) z = y
 *   D² 是二阶差分矩阵：[..., 1, -2, 1, ...]
 *   (D²ᵀ D²) 是五对角，元素 [1, -4, 6, -4, 1]（中间）
 */
function whittakerSmooth(y: number[], lambda: number, order: number = 2): number[] {
  const n = y.length
  if (n < order + 1) return [...y]

  // 构造 A = I + λ * Dᵀ D，其中 D 是 (n-order) × n 的 order 阶差分矩阵
  // A 是带状对称矩阵，半带宽 = order
  // 用 LDLᵀ 分解

  // 行 i, 列 j 上的元素：A[i][j] = δ_ij + λ * sum_k (Dᵀ D)[i][j]
  // (Dᵀ D)[i][j] = sum_k D[k][i] * D[k][j]
  // 对 D² 而言，D²[k][k] = 1, D²[k][k+1] = -2, D²[k][k+2] = 1，其余 0
  // (D²ᵀ D²)[i][j] = sum_k D²[k][i] * D²[k][j]
  // 简化的五对角元素（中间行）：[1, -4, 6, -4, 1]
  // 但边界处要单独算

  // 通用做法：先把 A 用密集矩阵存（对 n<=200 完全可承受），求解一次。
  // 我们 n 通常 ≤ 200（每分一行），可以承受。

  // 计算 DᵀD 的稀疏元素
  const A: number[][] = Array.from({ length: n }, () => new Array(n).fill(0))
  for (let i = 0; i < n; i++) A[i][i] = 1

  // 计算 D^order 矩阵的行
  const buildDifference = (k: number): number[] => {
    // 返回 D^k 算子的系数（长度 k+1），形如 [1, -2, 1] for k=2
    const coef = [1]
    for (let step = 0; step < k; step++) {
      const next = new Array(coef.length + 1).fill(0)
      for (let i = 0; i < coef.length; i++) {
        next[i] += coef[i]
        next[i + 1] -= coef[i]
      }
      for (let i = 0; i < next.length; i++) coef[i] = next[i]
      // 重新设置长度
      while (coef.length > next.length) coef.pop()
      if (coef.length < next.length) coef.push(...next.slice(coef.length))
    }
    return coef
  }

  const dCoef = buildDifference(order)
  // 对每一行 k of D（共 n-order 行），加入 λ * D[k]ᵀ * D[k] 到 A
  for (let k = 0; k <= n - order - 1; k++) {
    for (let i = 0; i < dCoef.length; i++) {
      for (let j = 0; j < dCoef.length; j++) {
        A[k + i][k + j] += lambda * dCoef[i] * dCoef[j]
      }
    }
  }

  // 解 A z = y，用对称带状 LDLᵀ 分解（这里直接用密集 Cholesky 即可）
  // Cholesky: A = L Lᵀ
  const L: number[][] = Array.from({ length: n }, () => new Array(n).fill(0))
  for (let i = 0; i < n; i++) {
    for (let j = 0; j <= i; j++) {
      let sum = A[i][j]
      for (let k = 0; k < j; k++) sum -= L[i][k] * L[j][k]
      if (i === j) {
        L[i][j] = Math.sqrt(Math.max(0, sum))
      } else {
        L[i][j] = sum / (L[j][j] || 1e-12)
      }
    }
  }

  // 前向：L u = y
  const u = new Array(n).fill(0)
  for (let i = 0; i < n; i++) {
    let sum = y[i]
    for (let k = 0; k < i; k++) sum -= L[i][k] * u[k]
    u[i] = sum / (L[i][i] || 1e-12)
  }
  // 反向：Lᵀ z = u
  const z = new Array(n).fill(0)
  for (let i = n - 1; i >= 0; i--) {
    let sum = u[i]
    for (let k = i + 1; k < n; k++) sum -= L[k][i] * z[k]
    z[i] = sum / (L[i][i] || 1e-12)
  }
  return z
}

function buildWhittakerModel(policy: YearPolicy, lambda: number = 1.0): DensityModel {
  const segs = orderedSegments(policy)
  if (segs.length === 0 || policy.top300_line === null || policy.qualification_line === null) {
    return buildUniformModel(policy)
  }

  // 步骤 1：构造每 1 分的"均匀派发密度"初值
  const lo = policy.qualification_line
  const hi = policy.top300_line
  const N = hi - lo
  const density0 = new Array(N).fill(0)
  for (const seg of segs) {
    const width = seg.upper - seg.lower
    const perScore = seg.people / width
    for (let sc = seg.lower; sc < seg.upper; sc++) {
      const idx = sc - lo
      if (idx >= 0 && idx < N) density0[idx] = perScore
    }
  }

  // 步骤 2：Whittaker 平滑（不带分段总量约束的快速版）
  const smoothed = whittakerSmooth(density0, lambda, 2)

  // 步骤 3：重新按分段做"总量重投影"：保证每段平滑后总和 = 原段人数
  //         这是 PCLM 简化版的关键一步：保留原始信息
  const finalDensity = new Array(N).fill(0)
  for (const seg of segs) {
    let sum = 0
    for (let sc = seg.lower; sc < seg.upper; sc++) {
      const idx = sc - lo
      if (idx >= 0 && idx < N) sum += Math.max(0, smoothed[idx])
    }
    if (sum <= 0) {
      // 退化为均匀
      const perScore = seg.people / (seg.upper - seg.lower)
      for (let sc = seg.lower; sc < seg.upper; sc++) {
        const idx = sc - lo
        if (idx >= 0 && idx < N) finalDensity[idx] = perScore
      }
    } else {
      const scale = seg.people / sum
      for (let sc = seg.lower; sc < seg.upper; sc++) {
        const idx = sc - lo
        if (idx >= 0 && idx < N) finalDensity[idx] = Math.max(0, smoothed[idx]) * scale
      }
    }
  }

  // 步骤 4：累计 CDF（从高分往低分）
  const cumDown = new Array(N + 1).fill(0)
  // cumDown[k] = sum of finalDensity[N-k .. N-1]
  // 即从最高分往下 k 步的累计
  for (let k = 1; k <= N; k++) {
    cumDown[k] = cumDown[k - 1] + finalDensity[N - k]
  }

  const cumulativeBelow = (score: number): number => {
    // 返回 (score, hi) 区间内的累计人数（即从 hi 向下到 score 的累计）
    if (score >= hi) return 0
    if (score < lo) return cumDown[N]
    const k = hi - score
    const kFloor = Math.floor(k)
    const kFrac = k - kFloor
    if (kFloor + 1 > N) return cumDown[N]
    return cumDown[kFloor] + kFrac * (cumDown[kFloor + 1] - cumDown[kFloor])
  }

  return {
    method: 'whittaker',
    cumulativePositionInBand: (score) => {
      const band = segs.find((s) => score >= s.lower && score < s.upper)
      if (!band) return score >= hi ? 0 : 1
      const top = cumulativeBelow(band.upper)
      const bot = cumulativeBelow(band.lower)
      const here = cumulativeBelow(score)
      const denom = bot - top
      if (denom <= 0) return 0
      return Math.max(0, Math.min(1, (here - top) / denom))
    },
    rankAtScore: (score) => {
      if (score >= hi) return Math.max(1, Math.round(300 * (hi - score + 1) / 10 + 1))
      if (score < lo) return Math.round(300 + cumDown[N])
      return Math.round(300 + cumulativeBelow(score))
    },
    densityAt: (score) => {
      if (score < lo || score >= hi) return 0
      const idx = Math.floor(score - lo)
      return finalDensity[idx] ?? 0
    },
    curve: () => {
      const points: Array<{ score: number; density: number }> = []
      for (let sc = lo; sc < hi; sc++) {
        const idx = sc - lo
        points.push({ score: sc, density: finalDensity[idx] ?? 0 })
      }
      return points
    }
  }
}

// ───────────────────────────────────────────────────────────────
// 工厂函数
// ───────────────────────────────────────────────────────────────
export function buildDensityModel(policy: YearPolicy, method: DensityMethod): DensityModel {
  switch (method) {
    case 'uniform':
      return buildUniformModel(policy)
    case 'pchip':
      return buildPchipModel(policy)
    case 'whittaker':
      return buildWhittakerModel(policy, 100) // λ 经验值，未来可校准
    default:
      return buildPchipModel(policy)
  }
}

export const DENSITY_METHOD_LABELS: Record<DensityMethod, string> = {
  uniform: '基线 · 均匀分布',
  pchip: 'PCHIP 单调插值',
  whittaker: '诺奖级 HP 滤波修匀'
}
